import React, { useState, useEffect, useMemo, memo } from "react";
import "../../App.css";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { customStyles } from "./../../helpers/StyleUtils";
import { bookingService, userService, locationService, companyService, talentService } from "../../api/apiService";
import { useCostCenters } from "../../contexts/CostCenterContext";

const MemoizedSelect = memo(Select);

function BookingDetailsPage() {
  // Cost centers are DB-managed (Settings page); the hook falls back to the file list
  const { constcenter1, constcenter2 } = useCostCenters();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [status, setStatus] = useState("");
  const [propsoedBy, setpropsoedBy] = useState("");
  const [kpi, setKpi] = useState("");
  const bookingId = new URLSearchParams(location.search).get("id");
  const [superAdminOptions, setSuperAdminOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedSet, setSelectedSet] = useState(null);
  // Talent fee: default day rate (Tagessatz) / hourly rate (Stundensatz) + optional
  // special/custom rate on the booking. KIND orders can be billed daily, hourly or per KPI.
  const [talentName, setTalentName] = useState("");
  const [talentDayRate, setTalentDayRate] = useState(null);
  const [talentHourlyRate, setTalentHourlyRate] = useState(null);
  const [customRateCheck, setCustomRateCheck] = useState(false);
  const [customRate, setCustomRate] = useState("");
  const [bookedHours, setBookedHours] = useState("");


  const setOptions = useMemo(() => [
    { value: "On Model", label: "On Model" },
    { value: "On Produkt", label: "On Produkt" },
  ], []);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [date, setDate] = useState("");
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [selectedCostCenterTwo, setSelectedCostCenterTwo] = useState(null);

  const bookingStatusOptions = useMemo(() => [
    { value: "Booked", label: "booked / Lano task or credit note created" },
    { value: "confirmed", label: "confirmed" },
    { value: "confirmedCreditNote", label: "confirmed / credit note pending" },
    { value: "new order", label: "new order" },
    { value: "requested", label: "requested" },
    { value: "declined request", label: "declined request" },
    { value: "declined", label: "declined" },
    { value: "canceled", label: "canceled" },
    { value: "optioned", label: "optioned" },
    { value: "optionSolved", label: "option released" },
    { value: "talentOnHold", label: "Talent On Hold" },
  ], []);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000);
      if (response.data && response.data.locations) {
        const formattedLocations = response.data.locations
          .filter(location => location.short !== 'OSB')
          .map(location => ({
            value: location.id,
            label: location.short || location.name, // Prioritize short name over full name
            locationData: location
          })).sort((a, b) => a.label.localeCompare(b.label));
        setLocationOptions(formattedLocations);
        return formattedLocations;
      } else {
        console.error("No location data found in API response");
        setLocationOptions([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocationOptions([]);
      return [];
    }
  };

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setNotFound(true);
        // console.log("No booking ID found");
        setLoading(false);
        return;
      }
      
      try {
        // console.log("Fetching booking details for ID:", bookingId);
        const response = await bookingService.getBookingById(bookingId);

        if (!response.data) {
          // console.log("Booking not found");
          setNotFound(true);
          setLoading(false);
          return;
        }

        const bookingData = response.data;
        setBookingDetails(bookingData);
        setStatus(bookingStatusOptions.find(option => option.value === bookingData.status));
        setStartTime(bookingData.start_time || bookingData.startTime || "");
        setEndTime(bookingData.end_time || bookingData.endTime || "");

        // Talent fee: pre-fill the special (custom) rate from the booking if it has one,
        // and fetch the talent's default day rate (Tagessatz) for display.
        const existingCustom = bookingData.custom_rate?.value ?? bookingData.customRate;
        if (existingCustom !== null && existingCustom !== undefined && existingCustom !== "") {
          setCustomRateCheck(true);
          setCustomRate(String(existingCustom));
        }
        // Hourly KIND orders carry the booked hours used to compute the fee.
        if (bookingData.booked_hours !== null && bookingData.booked_hours !== undefined && bookingData.booked_hours !== "") {
          setBookedHours(String(bookingData.booked_hours));
        }
        setTalentName(bookingData.talent_name || "");
        const talentId = bookingData.talent_id || bookingData.sql_talent_id || bookingData.talentId;
        if (talentId) {
          try {
            const talentRes = await talentService.getTalentById(talentId);
            const talent = talentRes?.data?.talent || talentRes?.data;
            if (talent) {
              setTalentName(talent.Name || talent.name || bookingData.talent_name || "");
              const rate = talent.Tagessatz ?? talent.rate ?? talent.daily_rate;
              if (rate !== null && rate !== undefined && rate !== "") {
                setTalentDayRate(Number(rate));
              }
              const hourRate = talent.Stundensatz ?? talent.stundensatz;
              if (hourRate !== null && hourRate !== undefined && hourRate !== "") {
                setTalentHourlyRate(Number(hourRate));
              }
            }
          } catch (talentError) {
            console.error("Error fetching talent rate:", talentError);
          }
        }

        setDate(bookingData.end_date ? new Date(bookingData.end_date).toISOString().split("T")[0] : "");

        // Set initial cost centers
        if (bookingData.cost_center1) {
          const matched = constcenter1.find(c => c.value === bookingData.cost_center1?.value || c.id === bookingData.cost_center1?.id || c.value === String(bookingData.cost_center1));
          setSelectedCostCenter(matched || null);
        }
        if (bookingData.cost_center2) {
          const matched = constcenter2.find(c => c.value === bookingData.cost_center2?.value || c.id === bookingData.cost_center2?.id || c.value === String(bookingData.cost_center2));
          setSelectedCostCenterTwo(matched || null);
        }

        if (bookingData.set) {
          const matchedSet = setOptions.find(s => s.value === bookingData.set || s.label === bookingData.set);
          setSelectedSet(matchedSet || { value: bookingData.set, label: bookingData.set });
        }

        // Fetch companies for client dropdown
        try {
          const companiesResponse = await companyService.getAllCompanies(1, 1000);
          const companiesData = companiesResponse.data?.companies || [];
          const formattedCompanies = companiesData.map(company => ({
            value: company.id,
            label: company.name,
          }));
          setClientOptions(formattedCompanies);

          if (bookingData.client) {
            const matched = formattedCompanies.find(c =>
              c.value === bookingData.client?.id ||
              c.label === bookingData.client?.label ||
              c.label === bookingData.client
            );
            setSelectedClient(matched || null);
          }
        } catch (companyError) {
          console.error("Error fetching companies:", companyError);
        }

        // Set KPI value - handle both object and string/number formats
        const kpiValue = bookingData.kpi;
        if (typeof kpiValue === 'object' && kpiValue !== null) {
          // console.warn('KPI is an object, using empty string:', kpiValue);
          setKpi('');
        } else {
          setKpi(kpiValue || '');
        }

        // Fetch locations and set initial location
        const locations = await fetchLocations();
        
        // Set initial location value
        if (bookingData.location && locations.length > 0) {
          if (typeof bookingData.location === 'object' && bookingData.location !== null) {
            const locationOption = locations.find(loc => 
              loc.value === bookingData.location.id || 
              loc.label === bookingData.location.name || 
              loc.label === bookingData.location.short
            );
            setSelectedLocation(locationOption || null);
          } else if (typeof bookingData.location === 'string') {
            const locationOption = locations.find(loc => 
              loc.label === bookingData.location || 
              loc.value === bookingData.location
            );
            setSelectedLocation(locationOption || null);
          }
        }

        // Fetch users who can be assigned as proposedBy
        try {
          // console.log("Fetching users for proposedBy dropdown...");
          const usersResponse = await userService.getAllUsers();
          // console.log("Users API response:", usersResponse);
          
          if (usersResponse && usersResponse.data) {
            let users = [];
            
            // Handle different response structures
            if (Array.isArray(usersResponse.data)) {
              users = usersResponse.data;
            } else if (usersResponse.data.users && Array.isArray(usersResponse.data.users)) {
              users = usersResponse.data.users;
            } else {
              // console.log("Unexpected user data structure:", usersResponse.data);
            }
            
            // console.log("Found users:", users);
            
            if (users.length > 0) {
              // Filter for superadmin users only
              const superAdminUsers = users.filter(user => {
                const role = user.role || user.userRole || user.user_role;
                // console.log(`User ${user.name || user.email} has role:`, role);
                return role === 'superadmin' || role === 'admin' || role === 'planning';
              });
              
              // console.log("Filtered superadmin users:", superAdminUsers);
              
              // Format users for dropdown - use email as value instead of ID
              const formattedUsers = superAdminUsers.map(user => ({
                value: user.email || user.userEmail || user.user_email || `user-${user.id}`,
                label: user.name || user.username || user.email || `User ${user.id}`
              }));
              
              // console.log("Formatted users for dropdown:", formattedUsers);
              setSuperAdminOptions(formattedUsers);
              
              // Set initial proposedBy value if it exists
              if (bookingData.proposedBy) {
                // console.log("Looking for proposedBy user with value:", bookingData.proposedBy);
                const selectedUser = formattedUsers.find(option => 
                  option.value === bookingData.proposedBy || 
                  option.label === bookingData.proposedBy ||
                  // Also check if proposedBy is an ID and find the corresponding user
                  (superAdminUsers.find(u => (u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)) && 
                   option.value === (superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.email ||
                                    superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.userEmail ||
                                    superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.user_email))
                );
                // console.log("Found proposedBy user:", selectedUser);
                setpropsoedBy(selectedUser || null);
              }
            } else {
              // console.log("No users found in response");
            }
          } else {
            // console.log("No user data in response");
          }
        } catch (userError) {
          console.error("Error fetching users:", userError);
          console.error("Error details:", userError.response?.data || userError.message);
          // Continue even if user fetch fails
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId, bookingStatusOptions, setOptions]);

  // Helper function to display location
  const getLocationDisplay = (bookingDetails) => {
    if (!bookingDetails || !bookingDetails.location) return "N/A";
    
    if (typeof bookingDetails.location === 'object' && bookingDetails.location !== null) {
      return bookingDetails.location.short || bookingDetails.location.name || bookingDetails.location.label || "N/A";
    } else if (typeof bookingDetails.location === 'string') {
      return bookingDetails.location;
    }
    
    return "N/A";
  };

  // Helper function to display time
  const getTimeDisplay = (bookingDetails, timeType) => {
    if (!bookingDetails) return "N/A";
    
    // Check multiple possible field names for start/end time
    if (timeType === 'start') {
      return bookingDetails.start_time || bookingDetails.startTime || "N/A";
    } else if (timeType === 'end') {
      return bookingDetails.end_time || bookingDetails.endTime || "N/A";
    }
    
    return "N/A";
  };

  // Helper function to display KPI as integer
  const getKPIDisplay = (bookingDetails) => {
    if (!bookingDetails || !bookingDetails.kpi) return "N/A";
    
    const kpiValue = bookingDetails.kpi;
    
    // Handle object KPI (shouldn't happen but just in case)
    if (typeof kpiValue === 'object' && kpiValue !== null) {
      return "N/A";
    }
    
    // Convert to number and round to integer
    const numericValue = parseFloat(kpiValue);
    if (!isNaN(numericValue)) {
      return Math.round(numericValue).toString();
    }
    
    return "N/A";
  };

  const updateStatus = async () => {
    if (status) {
      try {
        await bookingService.updateBookingStatus(bookingId, status.value);
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating status:", error);
        alert("Failed to update status. Please try again.");
      }
    } else {
      alert("Please select a status before updating");
    }
  };

  const updatePropsedBy = async () => {
    if (propsoedBy) {
      try {
        // console.log("Updating proposedBy with email address:", propsoedBy.value);
        // console.log("Selected user object:", propsoedBy);
        
        const updateData = { 
          proposedBy: propsoedBy.value,  // Now contains email address instead of ID
          proposed_by: propsoedBy.value  // Send both formats in case API expects different field name
        };
        
        // console.log("Sending update data:", updateData);
        await bookingService.updateBooking(bookingId, updateData);
        
        // console.log("ProposedBy updated successfully");
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating proposed by:", error);
        console.error("Error details:", error.response?.data || error.message);
        alert("Failed to update proposed by. Please try again.");
      }
    } else {
      alert("Please select a user before updating");
    }
  };

  const updateKpi = async () => {
    try {
      // Convert KPI to integer if it's a valid number
      let kpiValue = kpi;
      if (kpiValue && !isNaN(parseFloat(kpiValue))) {
        kpiValue = Math.round(parseFloat(kpiValue)).toString();
      }
      
      await bookingService.updateBooking(bookingId, { kpi: kpiValue });
      // Reload page instead of showing success alert
      window.location.reload();
    } catch (error) {
      console.error("Error updating KPI:", error);
      alert("Failed to update KPI. Please try again.");
    }
  };

  const updateLocation = async () => {
    if (selectedLocation) {
      try {
        await bookingService.updateBooking(bookingId, { 
          location_id: selectedLocation.value // Send only the location ID
        });
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating location:", error);
        alert("Failed to update location. Please try again.");
      }
    } else {
      alert("Please select a location before updating");
    }
  };

  const updateClient = async () => {
    if (selectedClient) {
      try {
        await bookingService.updateBooking(bookingId, { client: selectedClient });
        window.location.reload();
      } catch (error) {
        console.error("Error updating client:", error);
        alert("Failed to update client. Please try again.");
      }
    } else {
      alert("Please select a client before updating");
    }
  };

  const updateDate = async () => {
    if (date) {
      try {
        await bookingService.updateBooking(bookingId, { end_date: date, start_date: date });
        window.location.reload();
      } catch (error) {
        console.error("Error updating date:", error);
        alert("Failed to update date. Please try again.");
      }
    } else {
      alert("Please enter a date before updating");
    }
  };

  const updateStartTime = async () => {
    try {
      await bookingService.updateBooking(bookingId, { start_time: startTime, startTime });
      window.location.reload();
    } catch (error) {
      console.error("Error updating start time:", error);
      alert("Failed to update start time. Please try again.");
    }
  };

  const updateEndTime = async () => {
    try {
      await bookingService.updateBooking(bookingId, { end_time: endTime, endTime });
      window.location.reload();
    } catch (error) {
      console.error("Error updating end time:", error);
      alert("Failed to update end time. Please try again.");
    }
  };

  const updateSet = async () => {
    if (selectedSet) {
      try {
        await bookingService.updateBooking(bookingId, { set: selectedSet.value });
        window.location.reload();
      } catch (error) {
        console.error("Error updating set:", error);
        alert("Failed to update set. Please try again.");
      }
    } else {
      alert("Please select a set before updating");
    }
  };


  const updateCostCenters = async () => {
    try {
      await bookingService.updateBooking(bookingId, {
        cost_center1: selectedCostCenter || null,
        cost_center2: selectedCostCenterTwo || null,
      });
      window.location.reload();
    } catch (error) {
      console.error("Error updating cost centers:", error);
      alert("Failed to update cost centers. Please try again.");
    }
  };

  // Update the talent fee. The rate unit follows the booking's billing type
  // (per "day", "hour" or "kpi"). When "Special Rate" is checked (or always, for KPI
  // billing which has no default talent rate) a custom_rate is stored on the booking;
  // otherwise null is sent so it falls back to the talent's default rate. For hourly
  // billing the booked hours are part of the fee, so they are updated here too.
  // Same shape used in ProposeTalentsBookingDetail; partial update via PUT /bookings/:id.
  const updateFee = async () => {
    const billingType = bookingDetails?.billing_type || "daily";
    const isHourly = billingType === "hourly";
    const isKpi = billingType === "kpi";
    const per = isKpi ? "kpi" : isHourly ? "hour" : "day";

    // KPI billing always needs an explicit rate per KPI; daily/hourly only when "Special Rate" is on.
    const needsRate = isKpi || customRateCheck;
    if (needsRate && (customRate === "" || isNaN(parseInt(customRate, 10)))) {
      alert(
        isKpi
          ? "Please enter the rate per KPI (numbers only)."
          : "Please enter a custom rate (numbers only) or uncheck Special Rate."
      );
      return;
    }
    if (isHourly && bookedHours !== "" && !(parseFloat(bookedHours) > 0)) {
      alert("Please enter a valid number of booked hours (greater than 0).");
      return;
    }
    try {
      const payload = {
        custom_rate: needsRate
          ? { value: parseInt(customRate, 10), currency: "EUR", per }
          : null,
      };
      if (isHourly && bookedHours !== "") {
        payload.booked_hours = parseFloat(bookedHours);
      }
      await bookingService.updateBooking(bookingId, payload);
      window.location.reload();
    } catch (error) {
      console.error("Error updating fee:", error);
      alert("Failed to update fee. Please try again.");
    }
  };

  const handleStatusChange = (selectedOption) => {
    setStatus(selectedOption);
  };

  const poposeClick = async () => {
    try {
      const response = await bookingService.getBookingById(bookingId);
      const booking = response.data;
      
      if (booking && !booking.isBeingProcessed) {
        // If the order is not being processed, update it to being processed
        await bookingService.updateBooking(bookingId, {
          isBeingProcessed: true,
          processingBy: localStorage.getItem("userEmail"),
        });
        navigate(
          `/admin/propose?bookingId=${bookingId}&group=${booking.group}`
        );
      } else {
        alert(
          "This order is being processed by another user."
        );
      }
    } catch (error) {
      console.error("Error checking booking status:", error);
      alert("Failed to process this booking. Please try again.");
    }
  };

  // ---- Compact UI style tokens (local to this page) — flat, no boxes, no radius ----
  const fieldCard = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    borderLeft: "1px solid #cfcfcf",
    padding: "10px 26px 18px 26px",
    minWidth: 0, // keep all grid columns equal width regardless of content
  };
  // Compact variant of the shared select styles (shorter control, no side margins)
  const compactSelect = {
    ...customStyles,
    control: (base, state) => ({
      ...customStyles.control(base, state),
      minHeight: "36px",
      height: "36px",
      marginLeft: 0,
      marginRight: 0,
      maxWidth: "none",
      textTransform: "none",
      fontWeight: 500,
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    placeholder: (base) => ({ ...base, color: "#888", fontWeight: 500 }),
    dropdownIndicator: (base, state) => ({ ...customStyles.dropdownIndicator(base, state), padding: "4px" }),
  };
  const fieldLabel = {
    margin: 0,
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#888",
  };
  const inputStyle = {
    width: "100%",
    padding: "9px 10px",
    border: "1px solid #d5d5d5",
    fontSize: "14px",
    boxSizing: "border-box",
  };
  const smallBtn = {
    alignSelf: "flex-start",
    padding: "8px 18px",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid #121212",
    background: "#fff",
    color: "#121212",
    cursor: "pointer",
  };
  const smallBtnGhost = {
    ...smallBtn,
    background: "#fff",
    color: "#121212",
  };

  // ---- Derived display values for the header / summary ----
  const positionText = bookingDetails?.position?.label || bookingDetails?.position?.name || "";
  const clientText = bookingDetails?.client?.label || bookingDetails?.client?.name || "Order";

  // ---- Billing model (KIND orders): daily (day rate) | hourly (hourly rate × hours) | kpi (rate per KPI × KPIs) ----
  const billingType = bookingDetails?.billing_type || "daily";
  const isHourlyBilling = billingType === "hourly";
  const isKpiBilling = billingType === "kpi";
  const perLabel = isKpiBilling ? "KPI" : isHourlyBilling ? "hour" : "day";
  // The default talent rate that matches this billing type (KPI has no default — the rate per KPI is set here).
  const defaultRateForType = isHourlyBilling ? talentHourlyRate : isKpiBilling ? null : talentDayRate;
  // The rate actually used: the custom rate when set/required, otherwise the talent default.
  const hasCustom = (isKpiBilling || customRateCheck) && customRate !== "" && !isNaN(Number(customRate));
  const effectiveRate = hasCustom ? Number(customRate) : defaultRateForType;
  const kpiCount = Number(bookingDetails?.kpi) || 0;
  const hoursNum = parseFloat(bookedHours);

  const feeText = (() => {
    if (effectiveRate === null || effectiveRate === undefined || isNaN(Number(effectiveRate))) return "N/A";
    const r = Number(effectiveRate);
    if (isKpiBilling) {
      return kpiCount > 0 ? `€ ${(r * kpiCount).toFixed(2)} (€${r}/KPI × ${kpiCount})` : `€ ${r} / KPI`;
    }
    if (isHourlyBilling) {
      return hoursNum > 0 ? `€ ${(r * hoursNum).toFixed(2)} (€${r}/h × ${hoursNum}h)` : `€ ${r} / h`;
    }
    return `€ ${r}`;
  })();
  const statusBadge = (() => {
    // Exact legend colors used across the app for order status
    const map = {
      "Booked": [173, 216, 230],          // booked / lano task or credit note created
      "confirmed": [21, 163, 38],          // confirmed
      "confirmedCreditNote": [15, 143, 143], // confirmed, Gutschrift still outstanding
      "new order": [255, 255, 255],        // new order
      "requested": [255, 136, 6],          // requested
      "optioned": [0, 96, 255],            // 1. optioned
      "optionSolved": [255, 255, 0],       // option - released
      "declined request": [255, 60, 0],    // declined request
      "declined": [255, 60, 0],
      "talentOnHold": [128, 0, 128],       // hold
      "canceled": [96, 96, 96],            // canceled / released
      "Canceled": [96, 96, 96],
    };
    const [r, g, b] = map[bookingDetails?.status] || [238, 240, 242];
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return {
      bg: `rgb(${r}, ${g}, ${b})`,
      fg: luminance < 0.6 ? "#fff" : "#222",
      border: luminance > 0.9 ? "1px solid #d5d5d5" : "none", // outline near-white badges
    };
  })();
  const summaryItems = bookingDetails
    ? [
        { k: "Client", v: clientText },
        { k: "Date", v: bookingDetails.end_date ? new Date(bookingDetails.end_date).toLocaleDateString() : "N/A" },
        { k: "Option", v: bookingDetails.option || "N/A" },
        { k: "Position", v: positionText || "N/A" },
        { k: "Set", v: bookingDetails.set || "N/A" },
        { k: "Location", v: getLocationDisplay(bookingDetails) },
        { k: "KPI", v: getKPIDisplay(bookingDetails) },
        { k: "Start", v: getTimeDisplay(bookingDetails, "start") },
        { k: "End", v: getTimeDisplay(bookingDetails, "end") },
        { k: "Billing", v: isKpiBilling ? "KPI" : isHourlyBilling ? "Hourly" : "Daily" },
        ...(isHourlyBilling ? [{ k: "Hours", v: hoursNum > 0 ? `${hoursNum} h` : "N/A" }] : []),
        { k: "Fee", v: feeText },
      ]
    : [];

  // Render the booking details if available
  return (
    <div className="content-wrapper page">
      {loading ? (
        <div className="content-wrapper">Loading Order details...</div>
      ) : notFound ? (
        <div className="content-wrapper">No such Order exists.</div>
      ) : bookingDetails ? (
        <div className="bdp">
          <style>{`
            .bdp .bdp-section-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #aaa; margin: 30px 0 12px; }
            .bdp .bdp-summary { display: flex; gap: 24px; align-items: stretch; }
            .bdp .bdp-summary .cell { flex: 1; min-width: 0; border-left: 1px solid #cfcfcf; padding-left: 10px; }
            .bdp .bdp-summary .cell .k { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #aaa; margin-bottom: 4px; }
            .bdp .bdp-summary .cell .v { font-size: 14px; color: #1d1d1d; font-weight: 600; word-break: break-word; }
            .bdp .bdp-row { display: grid; grid-template-columns: repeat(4, 1fr); column-gap: 28px; align-items: start; padding: 18px 12px; }
            .bdp .bdp-row:nth-child(even) { background: #f7f7f7; }
            .bdp button { transition: background .12s ease, color .12s ease, transform .05s ease; }
            .bdp button:hover { background: #121212 !important; color: #fff !important; }
            .bdp button:active { background: #000 !important; color: #fff !important; transform: translateY(1px); }
            .bdp input:focus { outline: none; border-color: #121212 !important; }
          `}</style>

          {/* Header */}
          <div
            className="content-wrapper"
            style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}
          >
            <div>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#aaa" }}>
                Order Details
              </p>
              <h1 style={{ margin: "6px 0 0" }}>
                {clientText}{positionText ? ` · ${positionText}` : ""}
              </h1>
              <p style={{ margin: "8px 0 0", color: "#888", fontSize: "13px" }}>
                {bookingDetails.end_date ? new Date(bookingDetails.end_date).toLocaleDateString() : "—"}
                {" · "}
                {getTimeDisplay(bookingDetails, "start")}–{getTimeDisplay(bookingDetails, "end")}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                {bookingDetails.set === "digital Production" && (
                  <span style={{ display: "inline-block", padding: "5px 0", fontSize: "12.5px", fontWeight: 800, color: "#121212" }}>
                    K.I.N.D.
                  </span>
                )}
                {(isHourlyBilling || isKpiBilling) && (
                  <span style={{ display: "inline-block", padding: "5px 12px", fontSize: "12.5px", fontWeight: 700, background: "#121212", color: "#fff" }}>
                    {isKpiBilling ? "KPI BILLING" : "HOURLY"}
                  </span>
                )}
                <span style={{ display: "inline-block", padding: "5px 12px", fontSize: "12.5px", fontWeight: 700, background: statusBadge.bg, color: statusBadge.fg, border: statusBadge.border }}>
                  {bookingDetails.status || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="content-wrapper" style={{ marginTop: "20px" }}>
            <div className="bdp-summary">
              {summaryItems.map((it) => (
                <div className="cell" key={it.k}>
                  <div className="k">{it.k}</div>
                  <div className="v">{it.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action bar + warning */}
          <div
            className="content-wrapper"
            style={{ marginTop: "20px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}
          >
            <button style={smallBtnGhost} onClick={poposeClick}>
              Propose other talent
            </button>
            <p
              style={{
                margin: 0,
                flex: "1 1 320px",
                color: "#9a4a00",
                fontSize: "12.5px",
                lineHeight: 1.4,
                padding: "10px 14px",
                backgroundColor: "#fff6ec",
                borderLeft: "3px solid #f0a04b",
              }}
            >
              ⚠ Updates here do <strong>not</strong> notify the talent — notify them manually. For confirmed orders, update the LANO order as well.
            </p>
          </div>

          {/* Editable fields — compact responsive flat grid */}
          <div className="bdp-section-title content-wrapper">Edit details</div>
          <div className="content-wrapper">
            {/* Row 1 */}
            <div className="bdp-row">
              <div style={fieldCard}>
                <p style={fieldLabel}>Status</p>
                <MemoizedSelect
                  value={status}
                  onChange={handleStatusChange}
                  options={bookingStatusOptions}
                  isClearable
                  placeholder="Select Status"
                  styles={compactSelect}
                />
                <button style={smallBtn} onClick={updateStatus}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>Propose By</p>
                <Select
                  value={propsoedBy}
                  onChange={setpropsoedBy}
                  options={superAdminOptions}
                  isClearable
                  placeholder="Select Proposed user"
                  styles={compactSelect}
                />
                <button style={smallBtn} onClick={updatePropsedBy}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>KPI</p>
                <input
                  type="text"
                  value={kpi}
                  onChange={(e) => setKpi(e.target.value)}
                  placeholder="Enter KPI"
                  style={inputStyle}
                />
                <button style={smallBtn} onClick={updateKpi}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>Location</p>
                <MemoizedSelect
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  options={locationOptions}
                  isClearable
                  placeholder="Select Location"
                  styles={compactSelect}
                />
                <button style={smallBtn} onClick={updateLocation}>Update</button>
              </div>
            </div>

            {/* Row 2 */}
            <div className="bdp-row">
              <div style={fieldCard}>
                <p style={fieldLabel}>Start Time</p>
                <input
                  type="text"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  placeholder="Start Time"
                  style={inputStyle}
                />
                <button style={smallBtn} onClick={updateStartTime}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>End Time</p>
                <input
                  type="text"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="End Time"
                  style={inputStyle}
                />
                <button style={smallBtn} onClick={updateEndTime}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>Set</p>
                <MemoizedSelect
                  value={selectedSet}
                  onChange={setSelectedSet}
                  options={setOptions}
                  isClearable
                  placeholder="Select Set"
                  styles={compactSelect}
                />
                <button style={smallBtn} onClick={updateSet}>Update</button>
              </div>

              <div style={fieldCard}>
                <p style={fieldLabel}>Client</p>
                <MemoizedSelect
                  value={selectedClient}
                  onChange={setSelectedClient}
                  options={clientOptions}
                  isClearable
                  placeholder="Select Client"
                  styles={compactSelect}
                />
                <button style={smallBtn} onClick={updateClient}>Update</button>
              </div>
            </div>

            {/* Row 3 */}
            <div className="bdp-row">
              <div style={fieldCard}>
                <p style={fieldLabel}>Date</p>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={inputStyle}
                />
                <button style={smallBtn} onClick={updateDate}>Update</button>
              </div>

              {/* Talent Fee (before Cost Centers) — unit follows the billing type (day / hour / KPI) */}
              <div style={fieldCard}>
                <p style={fieldLabel}>
                  Talent Fee{isHourlyBilling ? " · Hourly" : isKpiBilling ? " · KPI" : ""}
                </p>
                <div style={{ fontSize: "12.5px", color: "#666", lineHeight: 1.5 }}>
                  {talentName || "No talent yet"}
                  {isKpiBilling
                    ? " · billed per KPI (set the rate below)"
                    : defaultRateForType !== null
                    ? ` · default € ${defaultRateForType} / ${perLabel}`
                    : " · default N/A"}
                </div>

                {/* Hourly: booked hours are part of the fee, edit them here */}
                {isHourlyBilling && (
                  <div>
                    <p style={{ ...fieldLabel, marginBottom: "4px" }}>Booked hours</p>
                    <input
                      type="number"
                      min="0"
                      step="0.25"
                      value={bookedHours}
                      onChange={(e) => setBookedHours(e.target.value)}
                      placeholder="e.g. 3.5"
                      style={inputStyle}
                    />
                  </div>
                )}

                {/* Daily / hourly use a "Special Rate" toggle; KPI always needs an explicit rate per KPI */}
                {!isKpiBilling && (
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={customRateCheck}
                      onChange={() => setCustomRateCheck(!customRateCheck)}
                      style={{ width: "16px", height: "16px", margin: 0 }}
                    />
                    Special Rate
                  </label>
                )}
                {(isKpiBilling || customRateCheck) && (
                  <input
                    type="text"
                    value={customRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[0-9]*$/.test(value)) {
                        setCustomRate(value);
                      }
                    }}
                    placeholder={
                      isKpiBilling
                        ? "Rate per KPI in € — numbers only (required)"
                        : `Custom rate in € per ${perLabel} — numbers only`
                    }
                    style={inputStyle}
                  />
                )}

                {/* Live fee breakdown for hourly / KPI billing */}
                {(isHourlyBilling || isKpiBilling) && (
                  <div style={{ fontSize: "12px", color: "#888" }}>{feeText}</div>
                )}

                <button style={smallBtn} onClick={updateFee}>Update Fee</button>
              </div>

              {/* Cost Centers — spans two cards (two selects, one update button) */}
              <div style={{ ...fieldCard, gridColumn: "span 2" }}>
                <p style={fieldLabel}>Cost Centers</p>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <MemoizedSelect
                      value={selectedCostCenter}
                      onChange={setSelectedCostCenter}
                      options={constcenter1}
                      isClearable
                      placeholder="Cost Center 1"
                      styles={compactSelect}
                    />
                  </div>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <MemoizedSelect
                      value={selectedCostCenterTwo}
                      onChange={setSelectedCostCenterTwo}
                      options={constcenter2}
                      isClearable
                      placeholder="Cost Center 2"
                      styles={compactSelect}
                    />
                  </div>
                </div>
                <button style={smallBtn} onClick={updateCostCenters}>Update</button>
              </div>
            </div>
          </div>

        </div>
      ) : null}
    </div>
  );
}
export default BookingDetailsPage;
